import { ThemeProvider } from '@mui/material/styles';
import { SessionProvider } from 'next-auth/react';
import React from 'react';
import type { AppProps } from 'next/app';
import { GoogleAnalytics } from '@next/third-parties/google';
import type { Session } from 'next-auth';
import { CookiesProvider } from 'react-cookie';

import {
  DehydratedState,
  HydrationBoundary,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { AuthPageGate } from '@/config/auth';
import type { Permission } from '@/config/permissions';
import getTheme from '@/config/theme';
import CloseIcon from '@mui/icons-material/Close';
import CssBaseline from '@mui/material/CssBaseline';
import IconButton from '@mui/material/IconButton';
import { SnackbarProvider, closeSnackbar } from 'notistack';

import { buildBatchedGetRequest } from '@/hooks/base_hooks';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/pt-br';
import Head from 'next/head';
import { CustomSidebarProvider } from '@/components/layouts/CustomSidebar';
import { TrackingProvider } from '@/components/contexts/TrackingProvider';

type CustomAppProps = AppProps<{
  session: Session;
  dehydratedState?: unknown;
}> & {
  Component: AppProps['Component'] & {
    auth?: {
      isPublic?: boolean;
      permissions?: string[];
    };
  };
};

export default function App({
  Component,
  pageProps: { session, ...pageProps },
}: CustomAppProps) {
  const [queryClient] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({}),
        defaultOptions: {
          queries: {
            queryFn: ({ queryKey }) => {
              return buildBatchedGetRequest(
                queryKey as (string | number)[],
                {},
              )();
            },
            staleTime: 1000 * 60 * 3, // 30 seconds
          },
        },
      }),
  );

  const { theme } = getTheme();
  return (
    <SessionProvider session={session}>
      <CookiesProvider>
        <QueryClientProvider client={queryClient}>
          <HydrationBoundary
            state={pageProps.dehydratedState as DehydratedState | undefined}
          >
            <ThemeProvider theme={theme}>
              <TrackingProvider>
                <CustomSidebarProvider>
                  <ReactQueryDevtools />
                  <div id='theme-root'>
                    <CssBaseline />
                    <SnackbarProvider
                      autoHideDuration={3000}
                      preventDuplicate={true}
                      action={(snackbarId) => (
                        <IconButton
                          size='small'
                          aria-label='close'
                          color='inherit'
                          onClick={() => closeSnackbar(snackbarId)}
                        >
                          <CloseIcon fontSize='small' />
                        </IconButton>
                      )}
                    />
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale='pt-br'
                    >
                      <AuthPageGate
                        isPublic={Component?.auth?.isPublic}
                        permissions={
                          Component?.auth?.permissions as unknown as
                            | Permission
                            | Permission[]
                            | undefined
                        }
                      >
                        <Head>
                          <title>Radar das Escolas</title>
                        </Head>
                        <GoogleAnalyticsComponent />
                        <Component {...pageProps} />
                        {/* <CompletarPerfilPageGate
                          isPublic={Component?.auth?.isPublic}
                        >
                        </CompletarPerfilPageGate> */}
                      </AuthPageGate>
                    </LocalizationProvider>
                  </div>
                </CustomSidebarProvider>
              </TrackingProvider>
            </ThemeProvider>
          </HydrationBoundary>
        </QueryClientProvider>
      </CookiesProvider>
    </SessionProvider>
  );
}

function GoogleAnalyticsComponent() {
  const GA_ID = process.env.NEXT_PUBLIC_GA_ID;
  if (!GA_ID) return null;
  return <GoogleAnalytics gaId={GA_ID} />;
}
